import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  InputNumber,
  notification,
  Row,
  Select,
  Radio,
  Space,
  TimePicker,
  Divider,
} from "antd";
import { useForm } from "antd/es/form/Form";
import {
  ContainerResource,
  getContainers,
  insertManualRegister,
  RequestManualRegister,
} from "api/generated";
import { FormItem } from "common/components/form/FormItem";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import { useClient } from "hooks/useClient";
import React, { useState, useEffect, useMemo, useContext } from "react";
import dayjs, { Dayjs } from "dayjs";
import Validations from "./RegisterManualVolumeRules";
import { handleAxiosError } from "common/helpers/generalUtils";
import DistributionPointSelected from "context/MyDistributionPoint";

enum QuickDateOption {
  today = "today",
  yesterday = "yesterday",
  endLastMonth = "endLastMonth",
  custom = "custom",
}

enum VolumeUnit {
  percentage = "percentage",
  liters = "liters",
}

interface ManualVolumeRegisterDrawerProps {
  isVisible: boolean;
  onClose: (update: boolean) => void;
  defaultDate?: Dayjs;
}

function ManualVolumeRegisterDrawer({
  isVisible,
  onClose,
  defaultDate,
}: ManualVolumeRegisterDrawerProps) {
  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const [form] = useForm<RequestManualRegister>();
  const [containers, setContainers] = useState<ContainerResource[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState<Dayjs | null>(null);
  const [quickDateOption, setQuickDateOption] = useState<QuickDateOption>(
    QuickDateOption.today,
  );
  const [volumeUnit, setVolumeUnit] = useState<VolumeUnit>(
    VolumeUnit.percentage,
  );

  const isMeasurementAsPercentage = useMemo(
    () => volumeUnit === VolumeUnit.percentage,
    [volumeUnit],
  );

  function getDefaultDateTime(): Dayjs {
    if (defaultDate) {
      const hasSpecificTime = defaultDate.format("HH:mm:ss") !== "00:00:00";
      return hasSpecificTime
        ? defaultDate
        : defaultDate.hour(23).minute(59).second(59);
    }
    return dayjs().hour(23).minute(59).second(59);
  }

  const getInitialFormValues = (): Partial<RequestManualRegister> => {
    const initialValues: Partial<RequestManualRegister> = {
      registerVolumeTemperature: 20,
      registerVolumePressure: 10,
    };

    if (containers.length === 1) {
      initialValues.idContainer = containers[0].idContainer;
    }

    return initialValues;
  };

  useEffect(() => {
    setIsLoading(true);
    getContainers({
      client,
      path: {
        distributionPointId: distributionPoint?.id ?? "",
      },
    })
      .then(({ data }) => {
        setContainers(data ?? []);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [distributionPoint?.id, client]);

  useEffect(() => {
    if (isVisible) {
      const initialValues = getInitialFormValues();
      const defaultDateTime = getDefaultDateTime();

      setSelectedDateTime(defaultDateTime);
      setVolumeUnit(VolumeUnit.percentage);

      if (defaultDate) {
        setQuickDateOption(QuickDateOption.custom);
      }
      form.setFieldsValue(initialValues);
    }
  }, [isVisible, containers, defaultDate, form]);

  const getQuickDate = (option: QuickDateOption): Dayjs => {
    const dateMap: Record<QuickDateOption, () => Dayjs> = {
      [QuickDateOption.today]: () => dayjs(),
      [QuickDateOption.yesterday]: () => dayjs().subtract(1, "day"),
      [QuickDateOption.endLastMonth]: () =>
        dayjs().subtract(1, "month").endOf("month"),
      [QuickDateOption.custom]: () => dayjs(),
    };
    return dateMap[option]();
  };

  const handleQuickDateChange = (option: QuickDateOption): void => {
    setQuickDateOption(option);

    if (option === QuickDateOption.custom) return;

    const newDate = getQuickDate(option);
    const currentTime = selectedDateTime ?? newDate;
    const dateWithTime = newDate
      .hour(currentTime.hour())
      .minute(currentTime.minute())
      .second(currentTime.second());

    setSelectedDateTime(dateWithTime);
  };

  const handleDateChange = (date: Dayjs | null): void => {
    if (!date) return;

    const currentTime = selectedDateTime ?? dayjs();
    const newDateTime = date
      .hour(currentTime.hour())
      .minute(currentTime.minute())
      .second(currentTime.second());

    setSelectedDateTime(newDateTime);
    setQuickDateOption(QuickDateOption.custom);
  };

  const handleTimeChange = (time: Dayjs | null): void => {
    if (!time || !selectedDateTime) return;

    const newDateTime = selectedDateTime
      .hour(time.hour())
      .minute(time.minute())
      .second(time.second());

    setSelectedDateTime(newDateTime);
  };

  const handleVolumeUnitChange = (newVolumeUnit: VolumeUnit): void => {
    setVolumeUnit(newVolumeUnit);

    // Limpiar los campos cuando se cambia la unidad
    if (newVolumeUnit === VolumeUnit.liters) {
      form.setFieldValue("registerVolumePercentage", undefined);
    } else {
      form.setFieldValue("registerVolume", undefined);
    }
  };

  function prepareRequestData(
    formValues: RequestManualRegister,
  ): RequestManualRegister {
    const requestData: RequestManualRegister = {
      ...formValues,
      registerMeasurementDateTime: selectedDateTime?.format(
        "YYYY-MM-DD HH:mm:ss",
      ),
    };

    // Solo enviar el campo correspondiente según la unidad seleccionada
    if (volumeUnit === VolumeUnit.percentage) {
      requestData.registerVolume = undefined;
    } else {
      requestData.registerVolumePercentage = undefined;
    }

    return requestData;
  }

  function resetFormAndClose(wasRegistered: boolean): void {
    form.resetFields();
    setSelectedDateTime(null);
    setQuickDateOption(QuickDateOption.today);
    setVolumeUnit(VolumeUnit.percentage);
    onClose(wasRegistered);
  }

  function handleSubmit() {
    form
      .validateFields()
      .then((formValues) => {
        if (!selectedDateTime) {
          notification.error({
            message: "Error de validación",
            description: "La fecha y hora de medición son requeridas",
          });
          return;
        }
        const request = prepareRequestData(formValues);
        setIsLoading(true);

        insertManualRegister({
          client,
          body: request,
        })
          .then(({ data }) => {
            notification.success({
              message: "Éxito al registrar la medición de nivel",
              description: data,
            });

            resetFormAndClose(true);
          })
          .catch((err) =>
            handleAxiosError(
              err,
              "No fue posible registrar la medición de nivel",
            ),
          )
          .finally(() => {
            setIsLoading(false);
          });
      })
      .catch((err) => {
        handleAxiosError(err, "Error al validar la petición");
      });
  }

  const renderContainerOptions = (
    containers: ContainerResource[],
  ): React.ReactNode[] =>
    containers.map((item) => (
      <Select.Option key={item.idContainer} value={item.idContainer}>
        {item.containerKey}
      </Select.Option>
    ));

  const renderQuickDateOptions = (): React.ReactNode[] => [
    <Radio key={QuickDateOption.today} value={QuickDateOption.today}>
      Hoy ({dayjs().format("DD/MM/YYYY")})
    </Radio>,
    <Radio key={QuickDateOption.yesterday} value={QuickDateOption.yesterday}>
      Ayer ({dayjs().subtract(1, "day").format("DD/MM/YYYY")})
    </Radio>,
    <Radio
      key={QuickDateOption.endLastMonth}
      value={QuickDateOption.endLastMonth}
    >
      Fin mes anterior (
      {dayjs().subtract(1, "month").endOf("month").format("DD/MM/YYYY")})
    </Radio>,
    <Radio key={QuickDateOption.custom} value={QuickDateOption.custom}>
      Personalizada
    </Radio>,
  ];

  return (
    <Drawer
      title="Insertar registro manual"
      placement="right"
      onClose={() => {
        resetFormAndClose(false);
      }}
      loading={isLoading}
      open={isVisible}
      footer={
        <Button
          key="back"
          onClick={() => {
            resetFormAndClose(false);
          }}
          style={{ width: "100%" }}
        >
          Volver
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={24}>
          <ResponsiveCol size="xl">
            <FormItem<RequestManualRegister>
              label="Seleccione el contenedor"
              name="idContainer"
              rules={Validations.ContainerRules}
            >
              <Select placeholder="Seleccione un contenedor">
                {renderContainerOptions(containers)}
              </Select>
            </FormItem>
          </ResponsiveCol>
          <ResponsiveCol size="xl">
            <FormItem label="Selección rápida de fecha">
              <Radio.Group
                value={quickDateOption}
                onChange={(e) => handleQuickDateChange(e.target.value)}
                disabled={!!defaultDate}
              >
                <Space direction="vertical">{renderQuickDateOptions()}</Space>
              </Radio.Group>
            </FormItem>
          </ResponsiveCol>
          <ResponsiveCol size="xl">
            <FormItem label="Fecha de medición" rules={Validations.DateRules}>
              <DatePicker
                format="DD-MM-YYYY"
                placeholder="Seleccione la fecha"
                style={{ width: "100%" }}
                disabled={!!defaultDate}
                value={selectedDateTime}
                showNow={false}
                allowClear={false}
                onChange={handleDateChange}
              />
            </FormItem>
          </ResponsiveCol>
          <ResponsiveCol size="xl">
            <FormItem label="Hora de medición">
              <TimePicker
                format="HH:mm:ss"
                placeholder="Seleccione la hora"
                style={{ width: "100%" }}
                value={selectedDateTime}
                onChange={handleTimeChange}
                allowClear={false}
                showNow={false}
              />
            </FormItem>
          </ResponsiveCol>
          <ResponsiveCol size="xl">
            <FormItem<RequestManualRegister>
              label={
                isMeasurementAsPercentage
                  ? "Porcentaje actual del tanque"
                  : "Volumen actual del tanque"
              }
              name={
                isMeasurementAsPercentage
                  ? "registerVolumePercentage"
                  : "registerVolume"
              }
              rules={
                isMeasurementAsPercentage
                  ? Validations.VolumePercentageRules
                  : Validations.VolumeLitersRules
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                addonAfter={
                  <Select<VolumeUnit>
                    value={volumeUnit}
                    onChange={handleVolumeUnitChange}
                  >
                    <Select.Option value={VolumeUnit.liters}>
                      Litros
                    </Select.Option>
                    <Select.Option value={VolumeUnit.percentage}>
                      Porcentaje %
                    </Select.Option>
                  </Select>
                }
                min={0.0}
                max={isMeasurementAsPercentage ? 100.0 : undefined}
                precision={3}
                placeholder={
                  isMeasurementAsPercentage
                    ? "0.00 - 100.00"
                    : "Volumen en litros"
                }
              />
            </FormItem>
          </ResponsiveCol>
          <ResponsiveCol size="xl">
            <FormItem<RequestManualRegister>
              label="Temperatura observada"
              name="registerVolumeTemperature"
              rules={Validations.TemperatureRules}
            >
              <InputNumber
                style={{ width: "100%" }}
                precision={3}
                placeholder="Temperatura"
                addonAfter="°C"
              />
            </FormItem>
          </ResponsiveCol>
          <ResponsiveCol size="xl">
            <FormItem<RequestManualRegister>
              label="Presión observada"
              name="registerVolumePressure"
              rules={Validations.PressureRules}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0.0}
                precision={3}
                placeholder="Presión"
              />
            </FormItem>
          </ResponsiveCol>
          <ResponsiveCol size="xl">
            <FormItem<RequestManualRegister>
              label="Densidad observada"
              name="registerVolumeDensity"
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0.0}
                precision={3}
                placeholder="Densidad"
              />
            </FormItem>
          </ResponsiveCol>
          <Divider />
          <ResponsiveCol size="xl">
            <Button
              type="primary"
              onClick={() => {
                handleSubmit();
              }}
              icon={<PlusOutlined />}
              style={{ width: "100%" }}
              loading={isLoading}
            >
              Agregar Registro
            </Button>
          </ResponsiveCol>
        </Row>
      </Form>
    </Drawer>
  );
}

export default ManualVolumeRegisterDrawer;
